import { ArrowRight, Zap, Shield, Globe } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import StatsSection from '@/components/sections/StatsSection'
import TestimonialsSection from '@/components/sections/TestimonialsSection'
import Header from '@/components/layout/Header'
import { useState, useEffect } from 'react'
import { contactFormApi, landingStatsApi } from '@/services/api'
import { useToast } from '@/lib/use-toast'
import type { ContactFormRequest, LiveDashboardStats, IndustryStats } from '@/types'

export default function LandingPage() {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [liveDashboardStats, setLiveDashboardStats] = useState<LiveDashboardStats | null>(null)
  const [contactStats, setContactStats] = useState<IndustryStats | null>(null)
  const [statsLoading, setStatsLoading] = useState(true)
  const { toast } = useToast()

  useEffect(() => {
    const fetchLandingStats = async () => {
      try {
        setStatsLoading(true)
        const response = await landingStatsApi.getLandingStats()
        setLiveDashboardStats(response.liveDashboard)
        setContactStats(response.industryStats)
      } catch (err) {
        console.error('Error fetching landing stats:', err)
        // Fallback to demo data if API fails
        setLiveDashboardStats({
          activeLoads: 1247,
          connectedApis: 8,
          todaysUpdates: 342
        })
        setContactStats({
          activeUsers: 10000,
          activeUsersChange: '+12%',
          apiIntegrations: 50,
          apiIntegrationsChange: '+8',
          loadsProcessed: '2.4M+',
          loadsProcessedChange: '+23%',
          avgResponseTime: '<500ms',
          responseTimeChange: '-15%',
          systemUptime: '99.9%',
          systemUptimeChange: '+0.2%',
          dataAccuracy: '99.8%',
          dataAccuracyChange: '+1.1%',
          performanceScore: '98.5',
          performanceScoreChange: '+2.1%',
        })
      } finally {
        setStatsLoading(false)
      }
    }

    fetchLandingStats()
  }, [])

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsSubmitting(true)

    // Save form reference before async operation
    const form = e.currentTarget

    try {
      const formData = new FormData(form)
      const contactData: ContactFormRequest = {
        name: formData.get('name') as string,
        email: formData.get('email') as string,
        company: formData.get('company') as string,
        phoneNumber: formData.get('phoneNumber') as string,
        message: formData.get('message') as string,
      }

      const response = await contactFormApi.submitContactForm(contactData)

      if (response.success) {
        toast({
          variant: "success",
          title: "Message sent successfully!",
          description: "Thank you for your message! We will get back to you soon.",
        })
        // Reset form using saved reference
        form.reset()
      } else {
        throw new Error(response.message)
      }
    } catch (error) {
      console.error('Error submitting contact form:', error)
      toast({
        variant: "destructive",
        title: "Sending failed",
        description: error instanceof Error ? error.message : "A technical error occurred. Please try again or contact us another way.",
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="flex-1">
                {/* Hero Section */}
        <section className="relative w-full py-12 md:py-24 lg:py-32 xl:py-48 overflow-hidden border-0">
          {/* Background Image */}
          <div className="absolute inset-0">
            <div 
              className="absolute inset-0 bg-cover bg-center bg-no-repeat"
              style={{
                backgroundImage: "url('https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80')"
              }}
            ></div>
            {/* Blue overlay for brand consistency */}
            <div className="absolute inset-0 bg-gradient-to-br from-primary-900/90 via-primary-700/85 to-primary-800/90"></div>
          </div>
          

          
          <div className="container px-4 md:px-6 relative z-10">
            <div className="flex flex-col items-center space-y-8 text-center">
              <div className="space-y-6 animate-fade-in">
                <div className="inline-flex items-center rounded-full border px-4 py-2 text-sm bg-white/20 backdrop-blur-sm border-white/30 text-white">
                  <Zap className="mr-2 h-4 w-4" />
                  Revolutionizing Freight Management
                </div>
                <h1 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl lg:text-7xl/none text-white drop-shadow-2xl">
                  The Ultimate Freight
                  <br />
                  <span className="bg-gradient-to-r from-accent-400 to-accent-200 bg-clip-text text-transparent">
                    Aggregation Platform
                  </span>
                </h1>
                <p className="mx-auto max-w-[800px] text-white/90 md:text-xl lg:text-2xl leading-relaxed drop-shadow-lg">
                  Streamline your logistics operations with AIBoard. Aggregate data from multiple freight APIs, 
                  unify formats, and manage your cargo operations from one powerful platform.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row gap-4 animate-slide-in-from-bottom">
                <Button 
                  size="lg" 
                  className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105 px-8 py-4 text-lg"
                  onClick={() => window.open('https://dashboard.freightmaster.ai/', '_blank')}
                >
                  Launch Dashboard
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-white/30 text-white bg-white/10 backdrop-blur-sm hover:bg-white/20 hover:border-white shadow-lg hover:shadow-xl transition-all duration-300 px-8 py-4 text-lg"
                  onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                >
                  Explore Features
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section id="features" className="w-full py-16 md:py-24 lg:py-32 bg-white relative z-0 mt-0">
          <div className="container px-4 md:px-6">
            <div className="flex flex-col items-center justify-center space-y-8 text-center">
              <div className="space-y-6">
                <div className="inline-flex items-center rounded-full border px-6 py-3 text-sm bg-gradient-to-r from-primary-50 to-accent-50 border-primary-200 text-primary-700">
                  <Globe className="mr-2 h-4 w-4" />
                  Platform Capabilities
                </div>
                <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-primary-600 via-primary-700 to-accent-600 bg-clip-text text-transparent">
                  Powerful Features for
                  <br />
                  Modern Logistics
                </h2>
                <p className="max-w-[800px] text-neutral-600 text-lg md:text-xl leading-relaxed">
                  Everything you need to manage freight data efficiently and scale your logistics operations.
                </p>
              </div>
            </div>
                         <div className="mx-auto grid max-w-6xl items-center gap-8 py-12">
               <div className="flex flex-col justify-center space-y-6">
                 <div className="grid gap-6 md:grid-cols-1 lg:grid-cols-3">
                   <div className="group flex items-start gap-6 p-6 rounded-2xl bg-gradient-to-br from-white to-primary-50/30 border border-primary-100 hover:shadow-xl transition-all duration-300 hover:scale-105">
                     <div className="bg-gradient-to-br from-primary-500 to-primary-600 p-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                       <Globe className="h-7 w-7 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-primary-900 mb-2">Multi-API Integration</h3>
                       <p className="text-neutral-600 leading-relaxed">
                         Connect to multiple freight APIs and consolidate data in one unified dashboard.
                       </p>
                     </div>
                   </div>
                   <div className="group flex items-start gap-6 p-6 rounded-2xl bg-gradient-to-br from-white to-accent-50/30 border border-accent-100 hover:shadow-xl transition-all duration-300 hover:scale-105">
                     <div className="bg-gradient-to-br from-accent-500 to-accent-600 p-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                       <Zap className="h-7 w-7 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-accent-900 mb-2">Real-time Updates</h3>
                       <p className="text-neutral-600 leading-relaxed">
                         Get instant notifications and updates on freight availability and status changes.
                       </p>
                     </div>
                   </div>
                   <div className="group flex items-start gap-6 p-6 rounded-2xl bg-gradient-to-br from-white to-success-50/30 border border-success-100 hover:shadow-xl transition-all duration-300 hover:scale-105">
                     <div className="bg-gradient-to-br from-success-500 to-success-600 p-3 rounded-xl shadow-lg group-hover:shadow-xl transition-all duration-300">
                       <Shield className="h-7 w-7 text-white" />
                     </div>
                     <div>
                       <h3 className="text-xl font-bold text-success-900 mb-2">Enterprise Security</h3>
                       <p className="text-neutral-600 leading-relaxed">
                         Bank-level security with role-based access control and data encryption.
                       </p>
                     </div>
                   </div>
                 </div>
               </div>
               <div className="flex justify-center hidden">
                 <div className="grid gap-6 w-full max-w-md">
                   <Card className="bg-gradient-to-br from-white via-white to-primary-50/50 border-primary-200 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden">
                     <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-500/5"></div>
                     <CardHeader className="relative">
                       <div className="flex items-center gap-3 mb-2">
                         <div className="bg-primary-100 p-2 rounded-lg">
                           <Zap className="h-5 w-5 text-primary-600" />
                         </div>
                         <CardTitle className="text-primary-900">Live Dashboard</CardTitle>
                       </div>
                       <CardDescription className="text-neutral-600">
                         Monitor all your freight operations in real-time
                       </CardDescription>
                     </CardHeader>
                     <CardContent className="relative">
                       <div className="space-y-4">
                         <div className="flex justify-between items-center p-3 bg-white/70 rounded-lg border border-primary-100">
                           <span className="font-medium text-neutral-700">Active Loads</span>
                           <span className="font-bold text-2xl bg-gradient-to-r from-primary-600 to-primary-700 bg-clip-text text-transparent">
                             {statsLoading ? (
                               <div className="animate-pulse bg-neutral-200 h-8 w-16 rounded"></div>
                             ) : (
                               liveDashboardStats?.activeLoads.toLocaleString() || '1,247'
                             )}
                           </span>
                         </div>
                         <div className="flex justify-between items-center p-3 bg-white/70 rounded-lg border border-success-100">
                           <span className="font-medium text-neutral-700">Connected APIs</span>
                           <span className="font-bold text-2xl bg-gradient-to-r from-success-600 to-success-700 bg-clip-text text-transparent">
                             {statsLoading ? (
                               <div className="animate-pulse bg-neutral-200 h-8 w-8 rounded"></div>
                             ) : (
                               liveDashboardStats?.connectedApis || '8'
                             )}
                           </span>
                         </div>
                         <div className="flex justify-between items-center p-3 bg-white/70 rounded-lg border border-accent-100">
                           <span className="font-medium text-neutral-700">Today's Updates</span>
                           <span className="font-bold text-2xl bg-gradient-to-r from-accent-600 to-accent-700 bg-clip-text text-transparent">
                             {statsLoading ? (
                               <div className="animate-pulse bg-neutral-200 h-8 w-12 rounded"></div>
                             ) : (
                               liveDashboardStats?.todaysUpdates.toLocaleString() || '342'
                             )}
                           </span>
                         </div>
                       </div>
                     </CardContent>
                   </Card>
                 </div>
               </div>
             </div>
          </div>
        </section>

        {/* Stats Section */}
        <StatsSection />

        {/* Partners Section */}
        {/* <PartnersSection /> */}

        {/* About Section */}
        <section id="about" className="w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br from-neutral-50 to-white">
          <div className="container px-4 md:px-6">
            <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center">
              <div className="flex flex-col justify-center space-y-8">
                <div className="space-y-6">
                  <div className="inline-flex items-center rounded-full border px-4 py-2 text-sm bg-primary-50 border-primary-200 text-primary-700">
                    <Shield className="mr-2 h-4 w-4" />
                    Enterprise-Grade Platform
                  </div>
                  <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl bg-gradient-to-r from-neutral-900 to-neutral-700 bg-clip-text text-transparent">
                    Revolutionizing Freight Management
                  </h2>
                  <p className="max-w-[600px] text-neutral-600 text-lg md:text-xl leading-relaxed">
                    AIBoard consolidates freight data from multiple sources into a single, powerful platform. 
                    Built for logistics professionals who need efficiency, reliability, and scale.
                  </p>
                </div>
                <div className="flex flex-col gap-4 sm:flex-row">
                  <Button 
                    size="lg" 
                    className="bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-105"
                    onClick={() => window.open('https://dashboard.freightmaster.ai/', '_blank')}
                  >
                    Try This Now
                  </Button>
                  {/* <Button 
                    variant="outline" 
                    size="lg"
                    className="border-primary-300 text-primary-700 hover:bg-primary-50 shadow-md hover:shadow-lg transition-all duration-300"
                  >
                    View Pricing
                  </Button> */}
                </div>
              </div>
              <div className="flex flex-col gap-6">
                <Card className="bg-gradient-to-br from-white to-primary-50/30 border-primary-200 shadow-xl hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary-500/5 to-accent-500/5"></div>
                  <CardHeader className="relative">
                    <CardTitle className="flex items-center gap-3 text-primary-900">
                      <div className="bg-primary-100 p-2 rounded-lg">
                        <Shield className="h-6 w-6 text-primary-600" />
                      </div>
                      Key Benefits
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6 relative">
                    <div className="group flex items-start gap-4 p-4 rounded-xl bg-white/60 hover:bg-white/80 transition-all duration-300">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center">
                          <div className="h-3 w-3 rounded-full bg-white"></div>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-primary-900 mb-1">Unified Data Format</p>
                        <p className="text-neutral-600">Standardize data from different API sources</p>
                      </div>
                    </div>
                    <div className="group flex items-start gap-4 p-4 rounded-xl bg-white/60 hover:bg-white/80 transition-all duration-300">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-accent-500 to-accent-600 flex items-center justify-center">
                          <div className="h-3 w-3 rounded-full bg-white"></div>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-accent-900 mb-1">Advanced Filtering</p>
                        <p className="text-neutral-600">Filter by location, equipment, and custom parameters</p>
                      </div>
                    </div>
                    <div className="group flex items-start gap-4 p-4 rounded-xl bg-white/60 hover:bg-white/80 transition-all duration-300">
                      <div className="flex-shrink-0">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-br from-success-500 to-success-600 flex items-center justify-center">
                          <div className="h-3 w-3 rounded-full bg-white"></div>
                        </div>
                      </div>
                      <div>
                        <p className="font-semibold text-success-900 mb-1">Automated Workflows</p>
                        <p className="text-neutral-600">Set up notifications and automated responses</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials Section */}
        <TestimonialsSection />

        {/* Contact Section */}
        <section id="contact" className="relative w-full py-16 md:py-24 lg:py-32 bg-gradient-to-br from-primary-900 via-primary-600 to-accent-600 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-20">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(255,255,255,0.15)_1px,transparent_0)] bg-[length:20px_20px]"></div>
          </div>
          
          <div className="container px-4 md:px-6 relative">
            <div className="max-w-4xl mx-auto">
              {/* Section Header */}
              <div className="text-center space-y-6 mb-12">
                <div className="inline-flex items-center rounded-full border border-white/20 px-6 py-3 text-sm bg-white/10 backdrop-blur-sm text-white">
                  <ArrowRight className="mr-2 h-4 w-4" />
                  Get In Touch
                </div>
                <h2 className="text-4xl font-bold tracking-tighter sm:text-5xl md:text-6xl text-white drop-shadow-lg">
                  Ready to Transform Your
                  <br />
                  <span className="bg-gradient-to-r from-white to-primary-100 bg-clip-text text-transparent">
                    Logistics Operations?
                  </span>
                </h2>
                <p className="max-w-[700px] mx-auto text-primary-100 md:text-xl lg:text-2xl leading-relaxed">
                  Contact our team to learn how AIBoard can revolutionize your freight management.
                </p>
              </div>

              {/* Contact Form */}
              <div className="grid md:grid-cols-2 gap-12 items-center">
                {/* Form */}
                <div className="bg-white/10 backdrop-blur-md rounded-2xl p-8 border border-white/20">
                  <form className="space-y-6" onSubmit={handleContactSubmit}>
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-white mb-2">
                        Full Name *
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter your full name"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
                        Email Address *
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter your email address"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-white mb-2">
                        Company Name *
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter your company name"
                      />
                    </div>
                    <div>
                      <label htmlFor="phoneNumber" className="block text-sm font-medium text-white mb-2">
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        id="phoneNumber"
                        name="phoneNumber"
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Enter your phone number"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-white mb-2">
                        Message *
                      </label>
                      <textarea
                        id="message"
                        name="message"
                        rows={4}
                        required
                        disabled={isSubmitting}
                        className="w-full px-4 py-3 rounded-lg bg-white/20 border border-white/30 text-white placeholder-white/60 focus:outline-none focus:ring-2 focus:ring-white/50 focus:border-transparent transition-all duration-300 resize-none disabled:opacity-50 disabled:cursor-not-allowed"
                        placeholder="Tell us about your logistics needs..."
                      />
                    </div>
                    <Button 
                      type="submit"
                      size="lg" 
                      disabled={isSubmitting}
                      className="w-full bg-white text-primary-900 hover:bg-white/90 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:scale-105 py-4 text-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                    >
                      {isSubmitting ? 'Sending...' : 'Send Message'}
                      <ArrowRight className="ml-2 h-5 w-5" />
                    </Button>
                  </form>
                </div>

                {/* Contact Info & Trust Indicators */}
                <div className="space-y-8">
                  {/* Contact Information */}
                  <div className="space-y-6">
                    <h3 className="text-2xl font-bold text-white">Contact Information</h3>
                    <div className="space-y-4">
                      <div className="flex items-center text-primary-100">
                        <div className="w-6 h-6 mr-3 text-white">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                          </svg>
                        </div>
                        support@freightmaster.ai
                      </div>
                      <div className="flex items-center text-primary-100">
                        <div className="w-6 h-6 mr-3 text-white">
                          <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                          </svg>
                        </div>
                        +1 (224) 634-4556
                      </div>
                    </div>
                  </div>

                  {/* Trust indicators */}
                  <div className="grid grid-cols-2 gap-4 hidden">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-white/20 h-8 w-16 rounded mx-auto"></div>
                        ) : (
                          contactStats?.activeUsers.toLocaleString() + '+' || '10,000+'
                        )}
                      </div>
                      <div className="text-sm text-primary-100">Active Users</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-white/20 h-8 w-12 rounded mx-auto"></div>
                        ) : (
                          contactStats?.apiIntegrations + '+' || '50+'
                        )}
                      </div>
                      <div className="text-sm text-primary-100">API Integrations</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-white/20 h-8 w-16 rounded mx-auto"></div>
                        ) : (
                          contactStats?.loadsProcessed || '2.4M+'
                        )}
                      </div>
                      <div className="text-sm text-primary-100">Loads Processed</div>
                    </div>
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-4 text-center border border-white/20">
                      <div className="text-2xl font-bold text-white">
                        {statsLoading ? (
                          <div className="animate-pulse bg-white/20 h-8 w-16 rounded mx-auto"></div>
                        ) : (
                          contactStats?.avgResponseTime || '<500ms'
                        )}
                      </div>
                      <div className="text-sm text-primary-100">Response Time</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <footer className="bg-gradient-to-r from-neutral-900 to-neutral-800 text-white">
        <div className="container px-4 md:px-6 py-12">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            {/* Logo and Description */}
            <div className="md:col-span-2">
              <div className="flex items-center mb-4">
                <img src="/icon.svg" alt="AIBoard" className="h-8 w-8" />
                <span className="ml-2 text-2xl font-bold text-white">AIBoard</span>
              </div>
              <p className="text-neutral-300 mb-6 max-w-md leading-relaxed">
                The ultimate freight aggregation platform for logistics professionals. 
                Streamline operations, connect APIs, and manage cargo efficiently.
              </p>
              <Button 
                className="bg-primary-600 hover:bg-primary-700 text-white"
                onClick={() => window.open('https://dashboard.freightmaster.ai/', '_blank')}
              >
                Launch Dashboard
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
            
            {/* Quick Links */}
            <div>
              <h4 className="font-semibold text-white mb-4">Quick Links</h4>
              <nav className="flex flex-col space-y-3">
                <a href="#features" className="text-neutral-300 hover:text-primary-400 transition-colors">
                  Features
                </a>
                <a href="#about" className="text-neutral-300 hover:text-primary-400 transition-colors">
                  About
                </a>
              </nav>
            </div>
            
            {/* Legal */}
            <div>
              <h4 className="font-semibold text-white mb-4">Legal</h4>
              <nav className="flex flex-col space-y-3">
                <a href="/terms" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-primary-400 transition-colors">
                  Terms of Service
                </a>
                <a href="/privacy" target="_blank" rel="noopener noreferrer" className="text-neutral-300 hover:text-primary-400 transition-colors">
                  Privacy Policy
                </a>
                <a href="#contact" className="text-neutral-300 hover:text-primary-400 transition-colors">
                  Contact
                </a>
              </nav>
            </div>
          </div>
          
          {/* Bottom Bar */}
          <div className="border-t border-neutral-700 mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center">
            <p className="text-sm text-neutral-400">
              © 2025 AIBoard. All rights reserved.
            </p>
            <div className="flex space-x-6 mt-4 sm:mt-0">
              <div className="text-sm text-neutral-400">
                Mastering your Freight needs
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
} 